import { useId } from 'react';
import cx from 'classnames';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Modal } from '../Modal';
import { Button } from '../Button';

export type CustomizerScopeKind = 'device' | 'user' | 'organization';

export type CustomizerItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
};

export type CustomizerScope = {
  kind: CustomizerScopeKind;
  label: string;
  description?: string;
};

export type ViewCustomizerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Every item the view can show, in default order */
  items?: CustomizerItem[];
  /** Items shown, in order */
  visible?: string[];
  onVisibleChange?: (visible: string[]) => void;
  /** Further settings for the view (a default period, a density, ...) */
  children?: React.ReactNode;
  /** Where the arrangement can be saved; only those the user may write */
  scopes: CustomizerScope[];
  scope: CustomizerScopeKind;
  onScopeChange: (scope: CustomizerScopeKind) => void;
  onSave: () => void;
  onReset: () => void;
  saving?: boolean;
  t?: (key: string) => string;
};

const identity = (key: string) => key;

/**
 * One way to arrange any view: choose what it shows, put it in order, and say
 * whom the arrangement is for. Ordering uses buttons rather than dragging, so
 * it works the same with a mouse, a keyboard, a screen reader and a finger.
 */
export function ViewCustomizer({
  isOpen,
  onClose,
  title,
  items = [],
  visible = [],
  onVisibleChange,
  children,
  scopes,
  scope,
  onScopeChange,
  onSave,
  onReset,
  saving = false,
  t = identity,
}: ViewCustomizerProps) {
  const headingId = useId();
  const byId = new Map(items.map((item) => [item.id, item]));
  const shown = visible.filter((id) => byId.has(id));
  const hidden = items.filter((item) => !shown.includes(item.id));

  const move = (id: string, by: -1 | 1) => {
    const index = shown.indexOf(id);
    const target = index + by;
    if (index < 0 || target < 0 || target >= shown.length) return;
    const next = [...shown];
    [next[index], next[target]] = [next[target], next[index]];
    onVisibleChange?.(next);
  };
  const toggle = (id: string, on: boolean) =>
    onVisibleChange?.(on ? [...shown, id] : shown.filter((item) => item !== id));

  const row = (item: CustomizerItem, index: number | null) => {
    const on = index !== null;
    const checkboxId = `${headingId}-${item.id}`;
    return (
      <li
        key={item.id}
        className={cx(
          'flex items-center gap-3 rounded-lg border px-3 py-2',
          on
            ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            : 'border-dashed border-gray-200 dark:border-gray-700'
        )}
      >
        <input
          id={checkboxId}
          type="checkbox"
          checked={on}
          onChange={(e) => toggle(item.id, e.target.checked)}
          className="w-4 h-4 rounded accent-primary-600"
        />
        {item.icon && (
          <span className="w-5 h-5 inline-flex shrink-0" aria-hidden="true">
            {item.icon}
          </span>
        )}
        <label
          htmlFor={checkboxId}
          className={cx('flex-1 text-sm', on ? 'text-foreground' : 'text-foreground-secondary')}
        >
          {item.label}
        </label>
        {on && (
          <span className="flex gap-1">
            <button
              type="button"
              onClick={() => move(item.id, -1)}
              disabled={index === 0}
              aria-label={`${t('Move up')}: ${item.label}`}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <ArrowUp className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => move(item.id, 1)}
              disabled={index === shown.length - 1}
              aria-label={`${t('Move down')}: ${item.label}`}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <ArrowDown className="w-4 h-4" aria-hidden="true" />
            </button>
          </span>
        )}
      </li>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      aria-labelledby={headingId}
      footer={
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" onClick={onReset}>
            {t('Reset to default')}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button type="button" variant="primary" onClick={onSave} loading={saving}>
              {t('Save')}
            </Button>
          </div>
        </div>
      }
    >
      {{
        title: (
          <h2
            id={headingId}
            className="px-4 py-3 pr-14 text-lg font-semibold text-foreground border-b border-gray-200 dark:border-gray-700"
          >
            {title}
          </h2>
        ),
        content: (
          <div className="space-y-6" data-component="ViewCustomizer">
            {items.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-foreground mb-2">{t('Shown')}</h3>
                <ol className="space-y-1.5">{shown.map((id, index) => row(byId.get(id)!, index))}</ol>
                {hidden.length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold text-foreground mt-4 mb-2">
                      {t('Hidden')}
                    </h3>
                    <ul className="space-y-1.5">{hidden.map((item) => row(item, null))}</ul>
                  </>
                )}
              </section>
            )}

            {children}

            {scopes.length > 0 && (
              <fieldset>
                <legend className="text-sm font-semibold text-foreground mb-2">{t('Save for')}</legend>
                <div className="space-y-1.5">
                  {scopes.map((option) => (
                    <label
                      key={option.kind}
                      className={cx(
                        'flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer',
                        scope === option.kind
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      )}
                    >
                      <input
                        type="radio"
                        name={`${headingId}-scope`}
                        value={option.kind}
                        checked={scope === option.kind}
                        onChange={() => onScopeChange(option.kind)}
                        className="mt-1 accent-primary-600"
                      />
                      <span>
                        <span className="block text-sm text-foreground">{option.label}</span>
                        {option.description && (
                          <span className="block text-xs text-foreground-secondary">
                            {option.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </div>
        ),
      }}
    </Modal>
  );
}
